import { CompletePerson } from '@/app/models/person';
import { Project } from '@/app/models/project';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useCreateSharingMutation } from '@/app/services/sharingRequestApi';
import PageTitle from '@/components/pageTitle';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../common/GenericAutoComplete';
import PeriodSelect from '../uploadFiles/periodSelect';

interface NewEditTeacherPaymentProps {
  selectedPerson: CompletePerson | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewCreateSharingScreen(props: NewEditTeacherPaymentProps) {
  const { t } = useTranslation(['noSharing', 'common']);

  const { selectedPerson, setDrawerOpen } = props;

  const [selectedPeriodDate, setSelectedPeriodDate] = useState<dayjs.Dayjs | null>(dayjs());

  const [projectSearch, setProjectSearch] = useState('');
  const [project, setProject] = useState<Project | null>(null);

  const { data: projects, isFetching: isLoadingProjects } = useSearchProjectsQuery(
    {
      searchTerm: projectSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
      bringClosed: true,
    },
    { skip: projectSearch.length < 1 },
  );

  const [createSharing] = useCreateSharingMutation();

  const handleProcess = async () => {
    if (!selectedPeriodDate?.date() || !selectedPerson?.badge || !project?.code) {
      return;
    }
    const newSharingRequest = {
      periodDate: selectedPeriodDate?.format('YYYY-MM-DD') || '',
      badge: selectedPerson?.badge,
      projectCode: project?.code,
    };
    toast.info(
      t('adding.sharing', {
        position: toast.POSITION.TOP_RIGHT,
      }),
    );
    const response = await createSharing(newSharingRequest);
    if ('data' in response) {
      toast.success(
        t('sharing.added', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  return (
    <div
      style={{
        width: 480,
        backgroundColor: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: '24px',
      }}
    >
      <PageTitle title={t('add.sharing')} />
      <div style={{ marginBottom: '24px' }} />
      <PeriodSelect
        selectedPeriodDate={selectedPeriodDate}
        setSelectedPeriodDate={setSelectedPeriodDate}
      />
      <div style={{ marginBottom: '24px' }} />

      <Box>
        <Box sx={{ flexGrow: 1, width: '50%' }}>
          <Typography variant="h3">{t('common:collaborator')}</Typography>
          <TextField
            value={selectedPerson?.name}
            variant="outlined"
            disabled
            size="small"
            sx={{ width: '100%', fontSize: '13px', marginTop: '4px' }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, width: '100%', marginTop: '24px' }}>
          <Typography variant="h3">{t('common:project')}</Typography>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <GenericAutoComplete
              value={project}
              onChange={(value) => {
                if (value) setProject(value);
              }}
              equalityTest={(option, value) => option?.code === value?.code}
              getOptionLabel={(option) => `${option?.name}`}
              onSearch={(value) => {
                setProjectSearch(value);
              }}
              options={projectSearch !== '' && projects?.content ? projects?.content : []}
              placeholder={t('enter.project.name.or.code') || ''}
              loading={isLoadingProjects}
              sx={{ width: '100%', marginTop: '4px' }}
            />
          </Box>
        </Box>
      </Box>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginBottom: '8px',
          marginTop: 'auto',
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="baseline"
          margin={3}
          spacing={2}
        >
          <Button
            variant="secondary"
            onClick={() => {
              setDrawerOpen(false);
            }}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleProcess();
              setDrawerOpen(false);
            }}
            disabled={!project}
          >
            {t('common:add')}
          </Button>
        </Stack>
      </div>
    </div>
  );
}
